import * as React from 'react';
import styled, { css } from 'styled-components/macro';
import { theme } from 'lib/style';
import CloseIcon from 'lib/images/CloseIcon';
import { Modal, ButtonSwitch, Dropdown } from 'lib/components';
import { billingCycles } from '../main/UpgradeContainer';
import { useState } from 'react';
import { useBillingContext } from 'lib/context';
import {
  PackageName,
  PackageWithAdditionalInfo,
} from 'lib/const/PackageAdditionalInfo';
import { Button } from 'react-covideo-common';

const ModalItem = styled.div`
  box-sizing: border-box;
  width: 592px;
  padding: 32px;
  min-height: 232px;
  background-color: ${theme.palette.white};
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  height: 24px;
  h2 {
    font-weight: bold;
    font-size: 18px;
    line-height: 24px;
    color: ${theme.palette.black_1_100};
    margin: 0;
  }
  align-items: center;
  justify-content: space-between;
  margin: 0;
  padding: 0;
  color: ${theme.palette.black_1_100};
`;

const CloseButtonWrapper = styled.label`
  display: flex;
  align-items: center;
  margin: 0;
  padding: 0;
  color: ${theme.palette.coal};
  &:hover {
    cursor: pointer;
  }
`;

const Content = styled.div``;

const ButtonsWrapper = styled.section`
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;
`;

const SwitchWrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  margin-top: 32px;
  .remove-margin {
    margin: 0;
  }
`;
type AdditionalInfoTextProps = {
  isMonthly?: boolean;
};
const AdditionalInfoText = styled.div<AdditionalInfoTextProps>`
  font-weight: 600;
  font-size: 12px;
  line-height: 20px;
  color: ${theme.palette.gray80};
  margin-left: 15px;
  ${({ isMonthly }) =>
    isMonthly &&
    css`
      color: ${theme.palette.covideoOrange100};
    `}
`;

const Label = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${theme.palette.gray80};
  margin: 32px 0 8px;
`;

const Warning = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${theme.palette.gray80};
  margin-top: -4px;
  margin-bottom: 16px;
`;

const SeatsDropdownWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const getSeatsForPackage = (packageId: number) => {
  switch (packageId) {
    case PackageName.TEAMS:
      return [3, 4, 5];
    default:
      return [1, 2, 3, 4, 5];
  }
};

type Props = {
  currentPackageDetails: PackageWithAdditionalInfo;
  newPackageDetails: PackageWithAdditionalInfo;
  onSubmit: (newPackageId: number, seats: number, isUpgrading: boolean) => void;
  handleModalClose: () => void;
  billingCycle: string;
  onChangeBillingCycle: (billingCycle: string) => void;
};

export const ModalUpgradeEditPackage = ({
  newPackageDetails,
  currentPackageDetails,
  onSubmit,
  handleModalClose,
  billingCycle,
  onChangeBillingCycle,
}: Props) => {
  const {
    state: {
      subscription: { totalActiveUsers, totalAllowedUsers },
    },
  } = useBillingContext();
  const isUpgrading = newPackageDetails?.id !== currentPackageDetails?.id;
  const isEditing = !isUpgrading;
  const minSeats = totalActiveUsers ? totalActiveUsers : 1;
  const maxSeats = totalAllowedUsers ? totalAllowedUsers : 1;

  const seatOptions = getSeatsForPackage(newPackageDetails.id).map(s => {
    return {
      value: s,
      label: `${s} user${s > 1 ? 's' : ''}`,
      isDisabled: isEditing && s < parseInt(minSeats.toString(), 10),
    };
  });
  const [seats, setSeats] = useState(seatOptions[0]);

  const isMonthly = billingCycle === 'monthly';
  const price = Math.round(
    isMonthly ? newPackageDetails.feeMonthly : newPackageDetails.feeYearly * 12
  );
  const fullPrice = newPackageDetails.multipleSeats
    ? seats.value * price
    : price;
  const cycleLabel = isMonthly ? '/month' : '/year';
  const title = isUpgrading
    ? `Upgrade to ${newPackageDetails.title} for $${price} ${cycleLabel}/user`
    : 'Edit Subscription';
  const text = isUpgrading
    ? `Upgrade for $${fullPrice}${cycleLabel}`
    : 'Update Subscription';

  return (
    <Modal>
      <ModalItem>
        <Header>
          <h2>{title}</h2>
          <CloseButtonWrapper>
            <CloseIcon
              width={'24px'}
              height={'24px'}
              onClick={handleModalClose}
            />
          </CloseButtonWrapper>
        </Header>
        <Content>
          {newPackageDetails.multipleSeats && (
            <>
              <Label>Number of seats</Label>
              {isEditing && (
                <Warning>Can’t be lower than number of active users.</Warning>
              )}
              <SeatsDropdownWrapper>
                <Dropdown
                  menuPortalTarget={document.body}
                  menuPosition='fixed'
                  menuPlacement={'bottom'}
                  width={188}
                  value={seats}
                  options={seatOptions}
                  onChange={setSeats}
                />
                {isEditing && (
                  <AdditionalInfoText>
                    You have {minSeats}/{maxSeats} active users
                  </AdditionalInfoText>
                )}
              </SeatsDropdownWrapper>
            </>
          )}
          <SwitchWrapper>
            <ButtonSwitch
              defaultValue={billingCycle}
              values={billingCycles}
              onChange={newValue => {
                onChangeBillingCycle(newValue);
              }}
              className='remove-margin'
            />
          </SwitchWrapper>
        </Content>
        <ButtonsWrapper>
          <Button
            text={text}
            onClick={() =>
              onSubmit(
                newPackageDetails.id,
                newPackageDetails.id == 1 || newPackageDetails.id == 2
                  ? 1
                  : seats.value,
                isUpgrading
              )
            }
          />
        </ButtonsWrapper>
      </ModalItem>
    </Modal>
  );
};
